import React, { useMemo } from 'react';
import '../styles/CompaniesTable.css';
import { format, fromUnixTime } from 'date-fns';
import { colour, formatterLong, numberFormatter } from '../styles/GlobalStyles';
import { Badge } from './';
import { SortArrows } from '../assets/icons';

function CompaniesTable(props) {
    const data = props.data;
    const filteredData = props.filteredData || data;
    const setShowUsersPanel = props.setShowUsersPanel;
    const setCompanyUsersId = props.setCompanyUsersId;
    const onViewCustomerReports = props.onViewCustomerReports;
    const sortColumn = props.sortColumn || 'companyName';
    const sortDirection = props.sortDirection || 'asc';
    const handleSort = props.handleSort;
    const hoveredColumn = props.hoveredColumn;
    const setHoveredColumn = props.setHoveredColumn;
    const adminCompanyIds = JSON.parse(process.env.REACT_APP_ADMIN_COMPANY_IDS);

    const headings = [
        {
            preIcon: null,
            label: "Company",
            postIcon: null,
            sortKey: 'companyName'
        },
        {
            preIcon: null,
            label: "Subscription status",
            postIcon: null,
            sortKey: 'subscriptionStatus'
        },
        {
            preIcon: null,
            label: "Price",
            postIcon: null,
            sortKey: 'price'
        },
        {
            preIcon: null,
            label: "Start date",
            postIcon: null,
            sortKey: 'startDate'
        },
        {
            preIcon: null,
            label: "End date",
            postIcon: null,
            sortKey: 'endDate'
        },
        {
            preIcon: null,
            label: "Stripe ID",
            postIcon: null,
            sortKey: null // Not sortable
        },
        {
            preIcon: null,
            label: "Credits remaining",
            postIcon: null,
            sortKey: 'credits'
        },
        {
            preIcon: null,
            label: "Users",
            postIcon: null,
            sortKey: 'users'
        },
        {
            preIcon: null,
            label: "Actions",
            postIcon: null,
            sortKey: null // Not sortable
        }
    ];

    const formatDate = (dateField) => {
        if (!dateField || !dateField._seconds) {
            return "N/A";
        }
        const seconds = dateField._seconds;
        if (isNaN(seconds) || seconds <= 0) {
            return "N/A";
        }
        try {
            return format(fromUnixTime(seconds), "MMM dd, yyyy");
        } catch (error) {
            return "N/A";
        }
    };

    const getSubscriptionStatus = (item) => {
        const autoBilling = item.data.autoBilling;
        const today = new Date();
        const todaySeconds = today.getTime() / 1000;
        const endDate = item.data.endDate?._seconds;
        
        // If endDate is invalid, default to expired
        if (!endDate || isNaN(endDate) || endDate <= 0) {
            return {
                status: "Expired",
                bgColour: "bg-colour-light",
                dotColour: colour.gray03
            };
        }
        
        if ( endDate > todaySeconds && autoBilling === true ) {
            const status = "Active";
            const bgColour = "bg-colour-jade-01";
            const dotColour = colour.jade02;
            return {
                status: status,
                bgColour: bgColour,
                dotColour: dotColour
            };
        }
        else if ( endDate > todaySeconds && autoBilling === false ) {
            const status = "Cancelled";
            const bgColour = "bg-colour-red-01";
            const dotColour = colour.red02;
            return {
                status: status,
                bgColour: bgColour,
                dotColour: dotColour
            };
        }
        else {
            const status = "Expired";
            const bgColour = "bg-colour-light";
            const dotColour = colour.gray03;
            return {
                status: status,
                bgColour: bgColour,
                dotColour: dotColour
            };
        }
    };

    const viewUsers = (id) => {
        setShowUsersPanel(true);
        setCompanyUsersId(id);
    };

    const viewCustomerReports = (id) => {
        if (onViewCustomerReports) {
            onViewCustomerReports(id);
        }
    };

    // Sort the filtered data
    const sortedData = useMemo(() => {
        if (!handleSort) return filteredData; // If no sorting handler, return as-is
        
        const sorted = [...filteredData].filter(item => !adminCompanyIds.includes(item.data.companyId));
        
        sorted.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortColumn) {
                case 'companyName':
                    aValue = a.data?.companyName?.toLowerCase() || "";
                    bValue = b.data?.companyName?.toLowerCase() || "";
                    return sortDirection === 'asc' 
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                
                case 'subscriptionStatus':
                    const statusA = getSubscriptionStatus(a).status;
                    const statusB = getSubscriptionStatus(b).status;
                    return sortDirection === 'asc'
                        ? statusA.localeCompare(statusB)
                        : statusB.localeCompare(statusA);
                
                case 'price':
                    aValue = a.data?.price || 0;
                    bValue = b.data?.price || 0;
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                
                case 'startDate':
                    aValue = a.data?.startDate?._seconds || 0;
                    bValue = b.data?.startDate?._seconds || 0;
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                
                case 'endDate':
                    aValue = a.data?.endDate?._seconds || 0;
                    bValue = b.data?.endDate?._seconds || 0;
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                
                case 'credits':
                    aValue = a.data?.credits || 0;
                    bValue = b.data?.credits || 0;
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                
                case 'users':
                    aValue = a.data?.users?.length || 0;
                    bValue = b.data?.users?.length || 0;
                    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                
                default:
                    return 0;
            }
        });
        
        return sorted;
    }, [filteredData, sortColumn, sortDirection, handleSort, adminCompanyIds]);

    return (
        <div className="companies-table-outer-container">
            <table 
                className="companies-table-container"
            >
                <thead className="">
                    <tr className="bg-colour-white">
                        {
                            headings.map((item, index) =>
                                <th 
                                    className={`companies-table-label-element ${item.sortKey ? 'companies-table-sortable-header' : ''}`}
                                    key={index}
                                    style={item.sortKey ? { cursor: 'pointer' } : {}}
                                    onClick={item.sortKey ? () => handleSort(item.sortKey) : undefined}
                                    onMouseEnter={item.sortKey && setHoveredColumn ? () => setHoveredColumn(item.sortKey) : undefined}
                                    onMouseLeave={item.sortKey && setHoveredColumn ? () => setHoveredColumn(null) : undefined}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span className="body-regular colour-secondary">
                                            {item.label}
                                        </span>
                                        {item.sortKey && (
                                            <SortArrows 
                                                fill={sortColumn === item.sortKey ? colour.gray06 : (hoveredColumn === item.sortKey ? colour.gray04 : colour.gray00)} 
                                                className={sortColumn === item.sortKey ? '' : 'opacity-50'} 
                                            />
                                        )}
                                    </div>
                                </th>
                            )
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        sortedData.map((item, index) =>
                            <tr 
                                key={index}
                                className=""
                            >
                                <td className="companies-table-value-element">
                                    <span 
                                        className="body-semibold colour-primary cursor-pointer"
                                        style={{ textDecoration: 'none' }}
                                        onClick={() => viewCustomerReports(item.id)}
                                    >
                                        {item.data.companyName}
                                    </span>
                                </td>
                                <td className="companies-table-value-element">
                                    <div className="companies-table-badge-container">
                                        <Badge
                                            bgColour={getSubscriptionStatus(item).bgColour}
                                            text={getSubscriptionStatus(item).status}
                                            statusDot={true}
                                            statusDotColour={getSubscriptionStatus(item).dotColour}
                                            textClass="body-semibold colour-primary"
                                            icon={null}
                                            tooltip={false}
                                            tooltipBody={null}
                                            tooltipPosition={null}
                                            iconLeft={false}
                                        />
                                    </div>
                                </td>
                                <td className="companies-table-value-element">
                                    <span className="body-regular colour-primary">
                                        {formatterLong.format(item.data.price).replace(".00", "")}
                                        {
                                            item.data.monthly === true ?
                                            "/month"
                                            :
                                            "/year"
                                        }
                                    </span>
                                </td>
                                <td className="companies-table-value-element">
                                    <span className="body-regular colour-primary">
                                        {formatDate(item.data.startDate)}
                                    </span>
                                </td>
                                <td className="companies-table-value-element">
                                    <span className="body-regular colour-primary">
                                        {formatDate(item.data.endDate)}
                                    </span>
                                </td>
                                <td className="companies-table-value-element">
                                    <span className="body-regular colour-primary">
                                        {item.data.id}
                                    </span>
                                </td>
                                <td className="companies-table-value-element">
                                    <span className="body-regular colour-primary">
                                        {numberFormatter.format(item.data.credits)}
                                    </span>
                                </td>
                                <td className="companies-table-value-element">
                                    <span 
                                        className="body-semibold colour-primary underline cursor-pointer"
                                        onClick={() => viewUsers(item.id)}
                                    >
                                        {item.data.users?.length || 0} users
                                    </span>
                                </td>
                                <td className="companies-table-value-element">

                                </td>
                            </tr>
                        )
                    }
                </tbody>
            </table>
        </div>
    )
};

export default CompaniesTable;